import {
  createAsyncThunk,
  type Action,
  type ThunkAction,
} from '@reduxjs/toolkit';
import { t } from 'i18next';

import {
  selectShouldContinueSignMailSession,
  selectSignMailRecordingStatus,
  selectSignMailStatus,
  signMailRecordingUploadUrlSelector,
  textStartSecondsSelector,
} from './signMailSelectors';
import {
  _resetSignMail,
  _transitionSignMailStatus,
  mailBoxIsFull,
  recordingErrorOccurred,
  recordingUploadCompleted,
  recordingUploadStarted,
  startSignmailSession,
  signMailSessionEnded,
} from './signMailSlice';
import { getDeafUserInformationRequest } from './signMailThunkHelpers';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import type { RootState } from 'features/app/store/store';
import {
  ConnectionChangeType,
  InterpretingSessionType,
  TerminationType,
} from 'features/call/call-base/enums';
import { CallDaoService } from 'features/call/call-base/services';
import {
  finishRequiredApiCallsPending,
  setRelayCallState,
  setTerminationType,
  requiredApiCallsPending,
} from 'features/call/call-base/store';
import {
  callIdSelector,
  callIdMatchesCurrentCall,
} from 'features/call/call-base/store/selectors';
import {
  deafPhoneNumberSelector,
  setDeafIsLoading,
  setDeafStatus,
} from 'features/call/call-deaf/store';
import {
  isPrimaryHearingConnectedSelector,
  primaryHearingPhoneNumberSelector,
} from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { hideLocalVideo, showLocalVideo } from 'features/local-video/store';
import { handleError, showNotification } from 'features/notification/store';
import { sessionIdSelector } from 'features/session/store';
import { videoRecordingSettings } from 'features/signmail/constants';
import {
  SignMailRecordingStatus,
  SignMailStatus,
} from 'features/signmail/enums';
import {
  getGreetingVideoUrl,
  canTransitionToSignMailStatus,
  canStartLocalSignMailSession,
  getLocalSignMailStartBlockReason,
  localSignMailStartBlockMessage,
} from 'features/signmail/helpers';
import type { VideoMessageData } from 'features/signmail/interfaces';
import {
  SignMailDaoService,
  SignMailEventBus,
  SignMailRecorderService,
} from 'features/signmail/services';
import {
  isSignMailConnectRecordedSelector,
  recordSignMailConnect,
  recordSignMailDisconnect,
} from 'features/reporting/store';
import {
  stopCallTimerCount,
  startCallTimerCount,
} from 'features/call/call-timer/store';
import { sendInfoToViThunk } from 'features/teaming/teaming-base/store';
import { CallNotificationType } from 'features/call/call-events/enums';

const sendSignMailAnalytics = (
  dispatch: (action: unknown) => unknown,
  method: string,
  message: string
) =>
  dispatch(
    sendAnalyticsInfo({
      Method: method,
      message,
      LogGroup: 'SignMail',
    })
  );

interface StartSignMailSessionAndPrepareGreetingPayload {
  callId?: number;
  preloadedVideoMsgData?: VideoMessageData;
}

export const sendSignMailConnectionCallState = createAsyncThunk(
  'call/sendSignMailConnectionCallState',
  async (
    connectionChangeType: ConnectionChangeType,
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const isHearingConnected = isPrimaryHearingConnectedSelector(state);
    const sessionLoginHistoryId = sessionIdSelector(state);
    dispatch(requiredApiCallsPending());
    try {
      const call = await CallDaoService.connectionStateHistory({
        callId,
        connectionChangeType,
        isHearingConnected,
        sessionLoginHistoryId,
        isDeafConnected:
          connectionChangeType ===
          ConnectionChangeType.AGENT_CONNECT_HEARING_TO_DEAF_MESSAGE,
        interpretingSessionType:
          InterpretingSessionType.HEARING_TO_DEAF_MESSAGE,
      });
      dispatch(setRelayCallState(call.CallState));
      return true;
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'sendSignMailConnectionCallState',
        })
      );
      return false;
    } finally {
      dispatch(finishRequiredApiCallsPending());
    }
  }
);
/**
 * Determines whether a new SignMail session should be started.
 * @returns {Promise<boolean>} - `true` if a new SignMail session should be started, `false` if a session is already in progress or AST owns SignMail.
 */
export const shouldStartNewSignMailSession =
  (): ThunkAction<Promise<boolean>, RootState, unknown, Action> =>
  async (_dispatch, getState) =>
    canStartLocalSignMailSession(getState());

/**
 * Starts a new SignMail session and prepares the greeting for the deaf user.
 * This function is responsible for the following tasks:
 * - Starts a valid SignMail session by dispatching the `startValidSignmailSession` action.
 * - If this is a hearing-to-deaf call, sets the termination type to `TerminationType.SignMail`.
 * - Fetches the deaf user's greeting information from the `SignMailDaoService`.
 * - Sets the SignMail status to either `SignMailStatus.GREETING` or `SignMailStatus.COUNTDOWN` based on the greeting information.
 *
 * @param {any} payload - The payload for the async thunk action.
 * @param {any} { dispatch, getState } - The dispatch and getState functions from the Redux store.
 * @returns {Promise<void>} - A Promise that resolves when the operation is complete.
 */
export const startSignMailSessionAndPrepareGreeting = createAsyncThunk(
  'signMail/startSignMailSessionAndPrepareGreeting',
  async (
    payload: StartSignMailSessionAndPrepareGreetingPayload | undefined,
    { dispatch, getState }
  ) => {
    const initialState = getState() as RootState;
    const callId = payload?.callId ?? callIdSelector(initialState);
    const rejectIfCannotStart = (state: RootState) => {
      if (!callIdMatchesCurrentCall(state, callId)) {
        dispatch(
          sendAnalyticsInfo({
            Method: 'startSignMailSessionAndPrepareGreeting',
            message:
              'Local SignMail start canceled because this call is no longer active.',
            capturedCallId: callId,
            currentCallId: callIdSelector(state),
            LogGroup: 'SignMail',
          })
        );
        return true;
      }

      const blockReason = getLocalSignMailStartBlockReason(state);
      if (!blockReason) {
        return false;
      }
      sendSignMailAnalytics(
        dispatch,
        'startSignMailSessionAndPrepareGreeting',
        localSignMailStartBlockMessage[blockReason]
      );
      return true;
    };

    if (rejectIfCannotStart(initialState)) {
      return;
    }
    try {
      let videoMsgData: VideoMessageData;

      if (payload?.preloadedVideoMsgData) {
        videoMsgData = payload.preloadedVideoMsgData;
      } else {
        const response = await SignMailDaoService.getDeafUserInformation(
          getDeafUserInformationRequest(initialState)
        );
        videoMsgData = response.VideoMsgData;
      }

      const latestState = getState() as RootState;
      if (rejectIfCannotStart(latestState)) {
        return;
      }

      const videoUrl = getGreetingVideoUrl(videoMsgData);
      dispatch(
        startSignmailSession({
          callId: latestState.call.id,
          greeting: {
            videoUrl,
            text: videoMsgData.GreetingText,
            greetingPreference: videoMsgData.GreetingPreference,
          },
        })
      );
      dispatch(setTerminationType(TerminationType.SignMail));
      ParticipantsStatusEventBus.deaf.$unavailable.next();
      ParticipantsStatusEventBus.deaf.$connectSignMail.next();

      if (videoMsgData.GreetingURL || videoMsgData.GreetingText) {
        dispatch(changeSignMailStatus(SignMailStatus.GREETING));
      } else {
        sendSignMailAnalytics(
          dispatch,
          'startSignMailSessionAndPrepareGreeting',
          'No greeting found attempting to start countdown'
        );
        dispatch(changeSignMailStatus(SignMailStatus.COUNTDOWN));
      }
    } catch (error) {
      const latestState = getState() as RootState;
      if (!callIdMatchesCurrentCall(latestState, callId)) {
        rejectIfCannotStart(latestState);
        return;
      }

      dispatch(
        handleError({
          error,
          methodName: 'prepareCustomerGreeting',
        })
      );

      if (getLocalSignMailStartBlockReason(latestState)) {
        return;
      }

      ParticipantsStatusEventBus.deaf.$disconnectSignMail.next();
      dispatch(resetSignMailState());
    }
  }
);
/**
 * Dispatches an action to change the SignMail status to COUNTDOWN.
 * This function is called when the greeting has ended, and the countdown
 * to the start of the SignMail recording should begin.
 */
export const startSignMailCountdown =
  (): ThunkAction<void, RootState, unknown, Action> => (dispatch) => {
    dispatch(changeSignMailStatus(SignMailStatus.COUNTDOWN));
  };

/**
 * Dispatches an action to finish the SignMail recording process.
 * This function is responsible for uploading the recorded video, setting the
 * necessary metadata, and updating the state to reflect the successful
 * completion of the recording.
 *
 * @param {object} - An object containing the text to be included in the
 * SignMail message.
 * @param {string} text - The text to be included in the SignMail message.
 */
export const finishRecording = createAsyncThunk(
  'signMail/finishRecording',
  async ({ text }: { text: string }, { dispatch, getState }) => {
    const rootState = getState() as RootState;
    const uploadUrl = signMailRecordingUploadUrlSelector(rootState);
    const callId = callIdSelector(rootState);
    const service = SignMailRecorderService.getInstance();
    try {
      const signMailCallId = rootState.signMail.signMailSessionCallId;
      // under normal circumstances, this should not happen this is one last check before we send off the recording in case thunks get dispatched out of order.
      if (callId === signMailCallId) {
        dispatch(
          sendAnalyticsInfo({
            Method: 'finishRecording',
            message: `Sign mail call id ${signMailCallId} matches with current call id ${callId} will send sign mail recording`,
          })
        );
      } else {
        dispatch(
          sendAnalyticsInfo({
            Method: 'finishRecording',
            message: `Sign mail call id ${signMailCallId} does not match with current call id ${callId} will not send sign mail recording`,
          })
        );
        return;
      }
      const deafPhoneNumber = deafPhoneNumberSelector(rootState);
      const hearingPhoneNumber = primaryHearingPhoneNumberSelector(rootState);
      const textStartSeconds = textStartSecondsSelector(rootState);
      const { file, duration } = await service.stop(callId);

      dispatch(recordingUploadStarted());
      dispatch(sendAnalyticsInfo({ message: 'Starting upload of sign mail' }));
      const messageId = await SignMailDaoService.uploadFile(uploadUrl, file);
      const signMailRecordingSettings = {
        Bitrate: videoRecordingSettings.videoBitsPerSecond!,
        Codec: 'H264',
        Level: '2.2',
        Profile: 'Baseline',
        TextStartSeconds: textStartSeconds.toString(),
        MessageSeconds: duration,
        Text: text
          ? `Phone: \r\n${hearingPhoneNumber}\r\nNote: \r\n${text}`
          : '',
        TextStopSeconds: duration.toString(),
        ToPhoneNumber: deafPhoneNumber,
        MessageId: messageId,
      };

      await SignMailDaoService.uploadUrlReady(
        callId,
        signMailRecordingSettings
      );

      dispatch(
        sendAnalyticsInfo({
          message: 'SignMail recording uploaded successfully',
          duration: duration,
          bitrate: videoRecordingSettings.videoBitsPerSecond!,
          textStartSeconds: textStartSeconds,
          messageId: messageId,
          textStopSeconds: duration,
          Method: 'finishRecording',
          FileSize: file.size,
        })
      );
      dispatch(
        showNotification({
          title: t('signMail.successfullySent'),
          severity: 'success',
        })
      );
    } catch (error) {
      dispatch(recordingErrorOccurred());
      dispatch(
        handleError({
          error,
          methodName: 'finishRecording',
        })
      );
      dispatch(resetSignMailState());
    } finally {
      dispatch(recordingUploadCompleted());
      dispatch(resetSignMailState());
    }
  }
);

/**
 * Changes the status of the SignMail feature.
 *
 * This function is an asynchronous thunk that dispatches actions to update the SignMail status in the application state.
 * It checks the current status and whether a valid SignMail session is in progress before transitioning to the new status.
 * If the transition is invalid or a valid session is not in progress, it resets the SignMail state and sends analytics information.
 * Otherwise, it hides the local video, transitions to the new status, and sends analytics information about the status change.
 *
 * @param status - The new SignMail status to transition to.
 * @returns A Promise that resolves when the status change is complete.
 */
export const changeSignMailStatus = createAsyncThunk(
  'signMail/changeSignMailStatus',
  async (status: SignMailStatus, { dispatch, getState }) => {
    const state = getState() as RootState;
    const currentStatus = selectSignMailStatus(state);
    const shouldContinueSignMailSession =
      selectShouldContinueSignMailSession(state);

    if (!canTransitionToSignMailStatus(currentStatus, status)) {
      dispatch(
        sendAnalyticsInfo({
          Method: 'changeSignMailStatus',
          message: `Invalid signmail status transition from ${currentStatus} to ${status}`,
          LogGroup: 'SignMail',
        })
      );
      dispatch(resetSignMailState());
      return;
    }

    if (!shouldContinueSignMailSession) {
      dispatch(
        sendAnalyticsInfo({
          Method: 'changeSignMailStatus',
          message: `Valid signmail session not in progress will not change signmail status to ${status}`,
          LogGroup: 'SignMail',
        })
      );
      dispatch(resetSignMailState());
      return;
    }

    dispatch(hideLocalVideo());
    dispatch(_transitionSignMailStatus(status));
    dispatch(
      sendAnalyticsInfo({
        Method: 'changeSignMailStatus',
        message: `SignMail status changed to ${status}`,
        LogGroup: 'SignMail',
      })
    );
  }
);

/**
 * Atomically starts local SignMail from Skip-to-Record.
 * Re-checks AST claim + session-in-progress, marks deaf unavailable (required
 * for COUNTDOWN), then starts session with no await between check and start
 * (closes TOCTOU vs AST claim).
 * @returns true if local countdown was started
 */
export const startSkippedSignMailSession =
  (): ThunkAction<boolean, RootState, unknown, Action> =>
  (dispatch, getState) => {
    if (!canStartLocalSignMailSession(getState())) {
      return false;
    }

    dispatch(setDeafStatus('unavailable'));
    const callId = callIdSelector(getState());
    ParticipantsStatusEventBus.deaf.$connectSignMail.next();
    dispatch(startSignmailSession({ callId }));
    dispatch(changeSignMailStatus(SignMailStatus.COUNTDOWN));
    return selectSignMailStatus(getState()) === SignMailStatus.COUNTDOWN;
  };

export const resetSignMailState = createAsyncThunk(
  'signMail/resetSignMailState',
  async (_, { dispatch }) => {
    dispatch(showLocalVideo());
    dispatch(_resetSignMail());
    const service = SignMailRecorderService.getInstance();
    service.destroy();
    dispatch(
      sendAnalyticsInfo({
        Method: 'resetSignMailState',
        message: 'SignMail state reset and service destroyed',
        LogGroup: 'SignMail',
      })
    );
  }
);

const reportSignMailDisconnect = createAsyncThunk(
  'signMail/reportSignMailDisconnect',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const hasReportedConnect = isSignMailConnectRecordedSelector(state);

    dispatch(stopCallTimerCount());
    if (!hasReportedConnect) {
      sendSignMailAnalytics(
        dispatch,
        'reportSignMailDisconnect',
        'SignMail connect not recorded; disconnect report skipped'
      );
      return;
    }

    dispatch(recordSignMailDisconnect());
    const wasSent = await dispatch(
      sendSignMailConnectionCallState(
        ConnectionChangeType.AGENT_DISCONNECT_HEARING_TO_DEAF_MESSAGE
      )
    ).unwrap();
    if (!wasSent) {
      return;
    }
    dispatch(
      sendInfoToViThunk(CallNotificationType.SIGN_MAIL_DISCONNECT_RECORDED)
    );
    sendSignMailAnalytics(
      dispatch,
      'reportSignMailDisconnect',
      'SignMail disconnect recorded'
    );
  }
);

export const handleMailboxFull = createAsyncThunk(
  'signMail/handleMailboxFull',
  async (_, { dispatch }) => {
    dispatch(mailBoxIsFull());
    dispatch(
      sendAnalyticsInfo({
        Method: 'SignMail Start',
        message: 'SignMail mailbox is full',
        LogGroup: 'SignMail',
      })
    );
    const service = SignMailRecorderService.getInstance();
    service.destroy();
    await dispatch(reportSignMailDisconnect()).unwrap();
  }
);

export const handleSignMailDisconnect = createAsyncThunk(
  'signMail/handleSignMailDisconnect',
  async (_, { dispatch }) => {
    try {
      dispatch(setDeafStatus('disconnected'));
      dispatch(setDeafIsLoading(false));
      await dispatch(reportSignMailDisconnect()).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleSignMailDisconnect',
        })
      );
    }
  }
);

export const handleSignMailConnect = createAsyncThunk(
  'signMail/handleSignMailConnect',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const isSignMailConnectRecorded = isSignMailConnectRecordedSelector(state);
    // if connect has already been recorded, do not record again report and return
    if (isSignMailConnectRecorded) {
      dispatch(
        sendAnalyticsInfo({
          Method: 'handleSignMailConnect',
          Message:
            'SignMail connect already recorded will not record again this may happen, not a problem',
          LogGroup: 'SignMail',
        })
      );
      return;
    }
    dispatch(
      sendSignMailConnectionCallState(
        ConnectionChangeType.AGENT_CONNECT_HEARING_TO_DEAF_MESSAGE
      )
    );
    dispatch(setTerminationType(TerminationType.SignMail));
    dispatch(startCallTimerCount());
    dispatch(recordSignMailConnect());
    dispatch(
      sendInfoToViThunk(CallNotificationType.SIGN_MAIL_CONNECT_RECORDED)
    );
    dispatch(
      sendAnalyticsInfo({
        Method: 'handleSignMailConnect',
        Message:
          'deaf leg connected by signmail and Agent Connect Hearing to Deaf Message event recorded',
        LogGroup: 'SignMail',
      })
    );
  }
);

export const endSignMailSession = createAsyncThunk(
  'signMail/endSignMailSession',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;

    dispatch(setDeafIsLoading(true));
    if (
      selectSignMailRecordingStatus(state) === SignMailRecordingStatus.RECORDING
    ) {
      SignMailEventBus.recordingFinished$.next();
    }
    ParticipantsStatusEventBus.deaf.$disconnectSignMail.next();
    dispatch(showLocalVideo());
    dispatch(signMailSessionEnded());
    dispatch(
      sendAnalyticsInfo({
        Method: 'endSignMailSession',
        message: 'SignMail session ended and local video displayed',
        LogGroup: 'SignMail',
      })
    );
  }
);
